/**
 * ClipboardService.ts
 * Service de gestion du presse-papiers avec support ShapeData (Zustand) et localStorage comme fallback
 * CORRIGÉ: Fonctionne maintenant avec le système React/Zustand au lieu de Konva.Node
 */
import { logger } from '../utils/ProfessionalLogger';
import { generateShapeId, type ShapeData } from '../types/ShapeTypes';

const CLIPBOARD_STORAGE_KEY = 'snapbooth_clipboard';

export interface ClipboardData {
  shapes: ShapeData[];
  timestamp: number;
  version: string; // Pour la compatibilité future
}

export class ClipboardService {
  private static instance: ClipboardService | null = null;
  private clipboardData: ClipboardData | null = null;

  private constructor() {}

  public static getInstance(): ClipboardService {
    if (!ClipboardService.instance) {
      ClipboardService.instance = new ClipboardService();
    }
    return ClipboardService.instance;
  }

  /**
   * Copie des shapes dans le presse-papiers
   * CORRIGÉ: Fonctionne avec ShapeData au lieu de Konva.Node
   */
  public async copy(shapes: ShapeData[]): Promise<boolean> {
    try {
      if (shapes.length === 0) {
        logger.warn('⚠️ Aucune shape à copier');
        return false;
      }

      const clipboardData: ClipboardData = {
        shapes: shapes.map(shape => ({ ...shape })), // Copie profonde
        timestamp: Date.now(),
        version: '1.0',
      };

      const serialized = JSON.stringify(clipboardData);

      // FIABILISATION : mémoriser TOUJOURS en interne, de façon SYNCHRONE, avant
      // toute API asynchrone. Un coller intra-app marche alors même si l'API
      // Clipboard système échoue (focus / permissions / absence de geste).
      this.clipboardData = clipboardData;
      try {
        localStorage.setItem(CLIPBOARD_STORAGE_KEY, serialized);
      } catch {
        /* localStorage indisponible : on garde au moins la copie en mémoire. */
      }

      // Best-effort : pousser aussi vers le presse-papiers système (autres onglets).
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(serialized);
        } catch (_clipboardError) {
          logger.warn('⚠️ API Clipboard système indisponible (copie interne conservée)');
        }
      }
      logger.info(`✅ ${shapes.length} shape(s) copiée(s)`);
      return true;
    } catch (error) {
      logger.error('❌ Erreur lors de la copie:', error);
      return false;
    }
  }

  /**
   * Colle des shapes depuis le presse-papiers
   * CORRIGÉ: Retourne ShapeData[] au lieu de Konva.Node
   */
  public async paste(offsetX: number = 20, offsetY: number = 20): Promise<ShapeData[]> {
    try {
      let serialized: string | null = null;

      // FIABILISATION : si l'API système renvoie un JSON Snapbooth valide on l'utilise
      // (coller depuis un autre onglet) ; sinon on retombe sur la copie interne. On
      // NE laisse PAS un contenu presse-papiers étranger écraser la copie intra-app.
      if (navigator.clipboard && navigator.clipboard.readText) {
        try {
          const sys = await navigator.clipboard.readText();
          if (sys && sys.includes('"shapes"')) {
            serialized = sys;
          }
        } catch (_clipboardError) {
          logger.warn('⚠️ API Clipboard système indisponible, fallback copie interne');
        }
      }

      // Priorité à la copie en mémoire si l'API système n'a rien donné d'exploitable.
      if (!serialized && this.clipboardData?.shapes?.length) {
        return this.buildPastedShapes(this.clipboardData.shapes, offsetX, offsetY);
      }

      // Fallback: utiliser localStorage ou données en mémoire
      if (!serialized) {
        serialized = localStorage.getItem(CLIPBOARD_STORAGE_KEY);
        if (serialized && !this.clipboardData) {
          // Parser les données depuis localStorage
          try {
            this.clipboardData = JSON.parse(serialized);
          } catch (e) {
            logger.error('Erreur lors du parsing du presse-papiers:', e);
            return [];
          }
        }
      } else {
        // Parser les données depuis l'API Clipboard
        try {
          this.clipboardData = JSON.parse(serialized);
        } catch (e) {
          logger.error('Erreur lors du parsing du presse-papiers:', e);
          return [];
        }
      }

      if (
        !this.clipboardData ||
        !this.clipboardData.shapes ||
        this.clipboardData.shapes.length === 0
      ) {
        logger.warn('⚠️ Aucune donnée dans le presse-papiers');
        return [];
      }

      return this.buildPastedShapes(this.clipboardData.shapes, offsetX, offsetY);
    } catch (error) {
      logger.error('❌ Erreur lors du collage:', error);
      return [];
    }
  }

  /**
   * Construit de nouvelles shapes (IDs neufs + décalage) à partir d'une source.
   */
  private buildPastedShapes(
    sourceShapes: ShapeData[],
    offsetX: number,
    offsetY: number
  ): ShapeData[] {
    const pastedShapes: ShapeData[] = sourceShapes.map((shape, index) => ({
      ...shape,
      id: generateShapeId(),
      x: (shape.x || 0) + offsetX * (index + 1),
      y: (shape.y || 0) + offsetY * (index + 1),
    }));
    logger.info(`✅ ${pastedShapes.length} shape(s) collée(s)`);
    return pastedShapes;
  }

  /**
   * Coupe des shapes (copie puis supprime)
   */
  public async cut(shapes: ShapeData[]): Promise<boolean> {
    const copied = await this.copy(shapes);
    // La suppression doit être gérée par l'appelant (via deleteShape)
    return copied;
  }

  /**
   * Vérifie si le presse-papiers contient des données
   */
  public hasData(): boolean {
    return (
      (this.clipboardData !== null && this.clipboardData.shapes.length > 0) ||
      localStorage.getItem(CLIPBOARD_STORAGE_KEY) !== null
    );
  }

  /**
   * Vide le presse-papiers
   */
  public clear(): void {
    this.clipboardData = null;
    try {
      localStorage.removeItem(CLIPBOARD_STORAGE_KEY);
    } catch (error) {
      logger.warn('⚠️ Erreur lors du nettoyage du presse-papiers:', error);
    }
  }
}
